/**
 * NeverBounce Former-Member Background Campaign — admin console.
 *
 * DARK by default (gated on featureFlags.neverbounceCampaignBackfill). When the flag is off — the
 * default in EVERY environment until the independent audit + a business go-ahead — this renders a clear
 * disabled notice and makes NO server calls. All counts come from the server; the UI never calls
 * NeverBounce directly and never manufactures numbers. Suppression is OFF for this campaign; validation
 * does not change membership or consent.
 *
 * SIMPLIFIED (2026-08-16): this is a ONE-TIME batch submission of the frozen, deduped cohort; the
 * one-time cost is accepted, so there is NO budget/ceiling/pilot-approval UI. After the snapshot is
 * finalized (state 'ready'), a single fingerprint-bound "Start run" authorizes the isolated worker to
 * submit the whole cohort exactly once (never resubmitting). The typed confirm here is a safety, not a
 * budget.
 */

import { useCallback, useState } from "react";
import featureFlags from "../utils/featureFlags";
import {
  type CampaignStatus,
  type CreditsInfo,
  neverbounceCampaignService,
} from "../services/neverbounceCampaign.service";

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        padding: "4px 0",
        borderBottom: "1px solid #eee",
      }}
    >
      <span style={{ color: "#555" }}>{label}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  );
}

export default function NeverBounceCampaignAdmin() {
  const enabled = featureFlags.neverbounceCampaignBackfill;

  const [campaignId, setCampaignId] = useState("");
  const [status, setStatus] = useState<CampaignStatus | null>(null);
  const [credits, setCredits] = useState<CreditsInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Typed confirmation for the single "Start run" action (must equal the exact frozen distinct count).
  const [typedConfirmStart, setTypedConfirmStart] = useState("");

  const run = useCallback(async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      await fn();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }, []);

  const refresh = useCallback(async (id: string) => {
    const res = await neverbounceCampaignService.status(id);
    setStatus(res.status);
    setCredits(res.credits);
  }, []);

  // Complete, paginated, reconciled export. Pulls every snapshot member by keyset (seq) until a short
  // page, asserts the accumulated row count equals snapshot_distinct_count (fails VISIBLY on any missing
  // page / drift), and downloads a CSV. The CSV is assembled from an ARRAY of per-page string chunks and
  // handed to the Blob constructor directly — never concatenated into one giant ~676k-row string (that
  // would double peak memory and risk an OOM). We keep the row array only for the snapshot-count
  // reconciliation, not for building the file. Never manufactures rows client-side.
  const runExport = useCallback(async (st: CampaignStatus) => {
    const pageSize = 5000;
    const esc = (v: string | number | null) => {
      let x = v == null ? "" : String(v);
      // CSV FORMULA-INJECTION neutralization: a cell whose first char is = + - @ (or a leading tab/CR)
      // can be executed as a formula by Excel/Sheets. Prefix such cells with an apostrophe so they are
      // rendered as literal text. Applied to EVERY exported cell (email + provider-result included).
      if (/^[=+\-@\t\r]/.test(x)) x = "'" + x;
      // Quote cells containing delimiters/quotes/newlines/tabs.
      return /[",\n\r\t]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x;
    };
    const chunks: string[] = [
      "seq,email_lower,member_state,skip_reason,verdict,provider_result\n",
    ];
    let rowCount = 0;
    let afterSeq = 0;
    let guard = 0;
    // Cap the loop generously above the ~676k cohort at this page size.
    for (; guard < 500; guard++) {
      const res = await neverbounceCampaignService.exportPage(
        st.id,
        afterSeq,
        pageSize,
      );
      const page = res.rows ?? [];
      if (page.length === 0) break;
      // Keyset must strictly advance, else we would loop / miss rows.
      const maxSeq = page[page.length - 1].seq;
      if (maxSeq <= afterSeq) {
        throw new Error(
          `Export aborted: keyset did not advance (seq ${maxSeq} <= ${afterSeq}).`,
        );
      }
      chunks.push(
        page
          .map((r) =>
            [
              r.seq,
              r.email_lower,
              r.member_state,
              r.skip_reason,
              r.verdict,
              r.provider_result,
            ].map(esc).join(",")
          )
          .join("\n") + "\n",
      );
      rowCount += page.length;
      afterSeq = maxSeq;
      if (page.length < pageSize) break;
    }
    const expected = st.snapshot_distinct_count ?? null;
    if (expected != null && rowCount !== expected) {
      throw new Error(
        `Export incomplete: got ${rowCount} rows but snapshot_distinct_count is ${expected}. Not downloading a partial export.`,
      );
    }
    // Blob from the array of chunks — no giant single-string join.
    const blob = new Blob(chunks, { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `email-validation-campaign-${st.id}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setMsg(
      `Exported ${rowCount} rows${
        expected != null ? ` (reconciled to snapshot ${expected})` : ""
      }.`,
    );
  }, []);

  if (!enabled) {
    return (
      <div style={{ maxWidth: 760, margin: "40px auto", padding: 24 }}>
        <h1>Email Validation Campaign</h1>
        <div
          style={{
            background: "#fff8e1",
            border: "1px solid #ffe082",
            borderRadius: 8,
            padding: 16,
            marginTop: 16,
          }}
        >
          <strong>This feature is disabled.</strong>
          <p style={{ marginTop: 8, color: "#555" }}>
            The Former-Member background validation campaign is a one-time
            backfill that stays dark until it passes an independent audit and the
            business gives the go-ahead. No campaign can be created or started
            from here while it is disabled.
          </p>
        </div>
      </div>
    );
  }

  const s = status;
  const distinct = s?.snapshot_distinct_count ?? null;
  const canStartRun =
    !!s &&
    s.state === "ready" &&
    !!s.cohort_fingerprint &&
    distinct != null &&
    distinct > 0 &&
    typedConfirmStart === String(distinct);

  return (
    <div style={{ maxWidth: 900, margin: "24px auto", padding: 24 }}>
      <h1>Email Validation Campaign (Former Members)</h1>
      <p style={{ color: "#555" }}>
        One-time validation and reporting.{" "}
        <strong>Auto-suppression is OFF</strong> for this campaign; validating an
        address never changes membership, consent, or suppression state. Each
        distinct address is submitted at most once and never resubmitted.
      </p>

      {err && (
        <div
          style={{
            background: "#ffebee",
            border: "1px solid #ef9a9a",
            padding: 12,
            borderRadius: 8,
            margin: "12px 0",
          }}
        >
          {err}
        </div>
      )}
      {msg && (
        <div
          style={{
            background: "#e8f5e9",
            border: "1px solid #a5d6a7",
            padding: 12,
            borderRadius: 8,
            margin: "12px 0",
          }}
        >
          {msg}
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          margin: "16px 0",
        }}
      >
        <input
          placeholder="Campaign ID"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          style={{ flex: 1, padding: 8 }}
        />
        <button
          disabled={busy || !campaignId}
          onClick={() => run(() => refresh(campaignId))}
        >
          Load
        </button>
        <button
          disabled={busy}
          onClick={() =>
            run(async () => {
              const res = await neverbounceCampaignService.create();
              setCampaignId(res.campaign_id);
              setMsg(`Created draft campaign ${res.campaign_id}`);
              await refresh(res.campaign_id);
            })
          }
        >
          Create draft
        </button>
      </div>

      {s && (
        <>
          <section
            style={{
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h2>Status</h2>
            <Row label="State" value={s.state} />
            <Row label="Criteria version" value={s.criteria_version} />
            <Row
              label="Cohort fingerprint"
              value={
                s.cohort_fingerprint
                  ? s.cohort_fingerprint.slice(0, 16) + "…"
                  : "—"
              }
            />
            <Row label="Snapshot distinct" value={s.snapshot_distinct_count} />
            <Row label="Batch size" value={s.batch_size} />
            <Row
              label="Auto-suppress"
              value={String(s.auto_suppress ?? false) + " (immutable)"}
            />
            <Row
              label="Members"
              value={JSON.stringify(s.member_state_counts ?? {})}
            />
            <Row
              label="Jobs"
              value={JSON.stringify(s.job_state_counts ?? {})}
            />
            <Row label="Skips" value={JSON.stringify(s.skip_counts ?? {})} />
            <Row
              label="Verdicts"
              value={JSON.stringify(s.verdict_counts ?? {})}
            />
            <Row
              label="NeverBounce paid credits"
              value={credits ? credits.paid_remaining : "unknown"}
            />
            {s.last_error && <Row label="Last error" value={s.last_error} />}
          </section>

          <section
            style={{
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h2>Snapshot (free)</h2>
            <p style={{ color: "#555" }}>
              Build/extend the immutable, deduped cohort snapshot in bounded
              pages, then finalize to freeze the fingerprint and distinct count.
              Finalizing moves the campaign to <code>ready</code>.
            </p>
            <button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  let after: string | null = null;
                  let total = 0;
                  let done = false;
                  let guard = 0;
                  while (!done && guard < 200) {
                    const p = await neverbounceCampaignService.snapshotPage(
                      s.id,
                      after,
                    );
                    total += p.scanned;
                    after = p.last_email;
                    done = p.done;
                    guard++;
                  }
                  setMsg(`Snapshot pages complete (scanned ~${total}).`);
                  await refresh(s.id);
                })
              }
            >
              Run snapshot to completion
            </button>{" "}
            <button
              disabled={busy || !s.snapshot_exhausted}
              title={s.snapshot_exhausted
                ? "Freeze the fully-paged cohort"
                : "Run snapshot to completion (done) before finalizing"}
              onClick={() =>
                run(async () => {
                  const f = await neverbounceCampaignService.finalizeSnapshot(
                    s.id,
                  );
                  if (f.ok === false) {
                    // Self-healing drift reset: newly-eligible addresses appeared since the snapshot was
                    // paged. The server has reset the cursor; re-run "Run snapshot to completion", then
                    // finalize again.
                    setMsg(
                      `Snapshot incomplete — new eligible addresses were detected and the snapshot was reset. Re-run "Run snapshot to completion", then finalize again.`,
                    );
                  } else {
                    setMsg(
                      `Finalized: ${f.snapshot_distinct_count} distinct emails. Campaign is now ready to start.`,
                    );
                  }
                  await refresh(s.id);
                })
              }
            >
              Finalize snapshot
            </button>
            {!s.snapshot_exhausted && (
              <span style={{ color: "#a00", marginLeft: 8, fontSize: 13 }}>
                Finalize is disabled until the snapshot is paged to completion.
              </span>
            )}
          </section>

          <section
            style={{
              border: "1px solid #ddd",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <h2>Start run</h2>
            <p style={{ color: "#555" }}>
              Available only at <strong>ready</strong>, once the snapshot is
              finalized. Starting the run authorizes the isolated worker to
              validate the entire frozen cohort exactly once, in batches, until
              it is drained. Bound to the exact frozen cohort fingerprint. There
              is no budget or ceiling — this is a one-time submission of every
              distinct address. Type the exact distinct count to confirm.
            </p>
            {s.state !== "ready"
              ? (
                <p style={{ color: "#999" }}>
                  Start unlocks when the campaign reaches <code>ready</code>{" "}
                  (current state: <code>{s.state}</code>).
                </p>
              )
              : (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <label>
                      Type “{distinct}” (the distinct count) to confirm:
                    </label>
                    <input
                      value={typedConfirmStart}
                      onChange={(e) => setTypedConfirmStart(e.target.value)}
                      style={{ width: 140, padding: 6 }}
                    />
                  </div>
                  <button
                    style={{ marginTop: 8 }}
                    disabled={busy || !canStartRun}
                    onClick={() =>
                      run(async () => {
                        if (!s.cohort_fingerprint) {
                          throw new Error(
                            "No cohort fingerprint — finalize the snapshot first",
                          );
                        }
                        const r = await neverbounceCampaignService.startRun(
                          s.id,
                          s.cohort_fingerprint,
                        );
                        setMsg(
                          r.ok
                            ? `Run started. The worker validates the cohort in batches until drained.`
                            : `Refused: ${r.reason}`,
                        );
                        await refresh(s.id);
                      })}
                  >
                    Start run ({distinct} addresses)
                  </button>
                </>
              )}
          </section>

          <section
            style={{ border: "1px solid #ddd", borderRadius: 8, padding: 16 }}
          >
            <h2>Controls</h2>
            <button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await neverbounceCampaignService.pause(
                    s.id,
                    "operator pause",
                  );
                  await refresh(s.id);
                })
              }
            >
              Pause
            </button>{" "}
            <button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await neverbounceCampaignService.resume(s.id);
                  await refresh(s.id);
                })
              }
            >
              Resume
            </button>{" "}
            <button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await neverbounceCampaignService.cancel(
                    s.id,
                    "operator cancel",
                  );
                  await refresh(s.id);
                })
              }
            >
              Cancel
            </button>{" "}
            <button
              disabled={busy}
              onClick={() =>
                run(async () => {
                  const v = await neverbounceCampaignService.verdictReport(
                    s.id,
                  );
                  setMsg(
                    `Verdicts: ${JSON.stringify(v.verdict_counts)} across ${v.resolved_members} resolved.`,
                  );
                })
              }
            >
              Verdict report
            </button>{" "}
            <button
              disabled={busy}
              onClick={() => run(() => runExport(s))}
            >
              Export (complete CSV)
            </button>
            <p style={{ color: "#777", fontSize: 13, marginTop: 8 }}>
              Export pages the full snapshot by keyset and reconciles the row
              count against the finalized distinct count; it refuses to download
              a partial file if any page is missing.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
